import React from 'react';
import { OVERLAY_CARD_MIN_HEIGHT, OVERLAY_CARD_STANDARD_MIN_HEIGHT } from '../../Util/formHeightUtils';
import FormSkeletonCard from './FormSkeletonCard';
import {
  SkeletonAbout,
  SkeletonBlock,
  SkeletonByline,
  SkeletonHero,
  SkeletonLine,
  SkeletonProgress,
  SkeletonStatus,
  SkeletonSupportersRow,
} from './SkeletonPieces';

/**
 * The overlay while its configuration loads (owner call 2026-10-23), in the
 * overlay's own two layouts and with OverlayElement's own shell classes:
 *
 *   - desktop (lg and up): the shell with the campaign pane on the left — the
 *     flush hero, title, by-line, bar, supporters row, About and Learn more —
 *     and the form card on the right;
 *   - phone: the campaign scroll (header row, title, by-line, hero, bar,
 *     supporters, About) with the pinned Give bar.
 *
 * `formType` is unknown until the response lands, so the form pane takes the
 * standard frame unless the caller knows better. `closeButton` is the real
 * Close, so a donor who can see this frame can always leave it.
 */
function CampaignPaneSkeleton() {
  return (
    <div className="flex min-h-0 flex-1 flex-col" aria-hidden="true">
      <SkeletonHero flush />
      <div className="flex flex-col gap-[var(--cs-space-4)] px-5 pb-5 pt-5">
        <div className="flex flex-col gap-[var(--cs-space-1)]">
          <SkeletonLine leading="var(--cs-leading-30)" className="w-3/4" />
          <SkeletonByline />
        </div>
        <div className="flex flex-col gap-[var(--cs-space-3)]">
          <SkeletonProgress />
          <SkeletonSupportersRow />
        </div>
        <SkeletonAbout />
      </div>
    </div>
  );
}

function CampaignScrollSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-[var(--cs-space-6)]" aria-hidden="true">
      <div className="flex flex-col gap-[var(--cs-space-3)]">
        <div className="flex flex-col gap-[var(--cs-space-4)]">
          <div className="flex flex-col gap-[var(--cs-space-1)]">
            <SkeletonLine leading="var(--cs-leading-30)" className="w-3/4" />
            <SkeletonByline logo={false} />
          </div>
          <SkeletonHero />
          <SkeletonProgress />
        </div>
        <SkeletonSupportersRow />
      </div>
      <SkeletonAbout leading="var(--cs-leading-24)" />
    </div>
  );
}

export default function OverlaySkeleton({ formType = 'EMBED_FORM', config = null, closeButton = null }) {
  return (
    <SkeletonStatus
      label="Loading donation form"
      className="overlay-element-container flex min-h-screen-safe flex-col"
      data-overlay-skeleton=""
    >
      {/* Desktop: the shell, two equal-height white panes. */}
      <div className="hidden w-full lg:flex lg:min-h-screen lg:items-center lg:justify-center lg:py-10">
        <div
          className="relative w-fit lg:flex lg:items-stretch lg:justify-center lg:gap-[var(--cs-space-2)] overlay-mode rounded-[var(--cs-card-radius)] border border-[color:var(--cs-border-control)] bg-[color:var(--cs-overlay-surface)] p-[var(--cs-space-2)] shadow-[shadow:var(--cs-modal-shadow)]"
          style={{ minHeight: formType === 'EMBED_FORM' ? OVERLAY_CARD_STANDARD_MIN_HEIGHT : OVERLAY_CARD_MIN_HEIGHT }}
        >
          {closeButton && <div className="absolute left-full top-0 z-50 ml-[var(--cs-space-2)]">{closeButton}</div>}
          <div className="relative flex w-full max-w-md flex-none flex-col min-h-0 overflow-hidden rounded-[var(--cs-card-radius)] border border-[color:var(--cs-border-control-subtle)] bg-[color:var(--cs-surface-0)] lg:w-[28rem] max-h-[calc(100vh-var(--cs-space-10)*2-var(--cs-space-2)*2)]">
            <CampaignPaneSkeleton />
          </div>
          <div className="flex w-full max-w-md flex-none flex-col overflow-hidden overlay-form-stretch rounded-[var(--cs-card-radius)] border border-[color:var(--cs-border-control-subtle)] bg-[color:var(--cs-surface-0)] lg:w-[28rem] max-h-[calc(100vh-var(--cs-space-10)*2-var(--cs-space-2)*2)]">
            <div className="page-container flex flex-1 flex-col" data-mode="overlay">
              <FormSkeletonCard formType={formType} config={config} mode="overlay" />
            </div>
          </div>
        </div>
      </div>

      {/* Phone: the campaign scroll and the pinned Give bar. */}
      <div className="block h-screen-safe overflow-hidden bg-[color:var(--cs-surface-1)] lg:hidden">
        <div className="h-full overflow-y-auto px-4 pt-1 pb-24">
          <div className="flex h-[var(--cs-size-header)] items-center justify-between gap-[var(--cs-space-2)]">
            <SkeletonBlock className="size-[var(--cs-size-logo)]" />
            {closeButton}
          </div>
          <div className="pt-[var(--cs-space-3)]">
            <CampaignScrollSkeleton />
          </div>
        </div>
        <div className="fixed bottom-0 left-0 right-0 z-[52] block lg:hidden" aria-hidden="true">
          <div
            className="w-full border-t border-[color:var(--cs-border-control-subtle)] bg-[color:var(--cs-surface-0)] px-4 pt-3"
            style={{ paddingBottom: 'calc(var(--cs-space-5) + env(safe-area-inset-bottom, 0px))' }}
          >
            <SkeletonBlock shape="control" className="h-[var(--cs-size-control)] w-full" />
          </div>
        </div>
      </div>
    </SkeletonStatus>
  );
}
